import type { AuthSession } from '@pointfive/contracts';
import { apiBinding, apiOrigin, client } from './DataProvider';

/** Who, if anyone, is signed in (WP-02, SEC-AUTH, ESC-005).
 *
 *  Split out of `DataProvider` because the two answer different questions and only one of them may run for a public
 *  visitor. The provider used to resolve a session *and* fetch the whole authenticated dataset in a single
 *  module-level promise, which meant importing the app was enough to start both — before any route had been matched,
 *  for `/card/:token` and `/portal` as much as for the trader's own screens.
 *
 *  Three bindings, and the difference between them is deliberate:
 *
 *  - **sim** — the in-process demo. There is no server and no session to have, so the app is always "signed in".
 *    Anything else would put a sign-in wall in front of a dataset that lives in the same process.
 *  - **http with a dev-login email** — the sandbox and the CI live lane. Establishes the session the way it always
 *    did. `packages/config` refuses to start a production server with the dev binding, so this cannot reach a
 *    deployment.
 *  - **http without one** — a real deployment. Asks the server and takes the answer. No session means the route
 *    guard sends the person to the signed-out surface; it does not mean fall back to mock data.
 */

const devLoginEmail = import.meta.env['VITE_DEV_LOGIN_EMAIL'] as string | undefined;

/** Where `/auth/*` lives. Empty in the deployed topology, where the edge proxies it and the cookie is first-party. */
export const AUTH_ORIGIN = apiBinding === 'http' ? apiOrigin : '';

export const SIGNED_OUT_PATH = '/signin';

export interface SessionState {
  readonly session: AuthSession | null;
  /** True when this build has no session concept at all, so the guard must not gate anything. */
  readonly unguarded: boolean;
}

let pending: Promise<SessionState> | null = null;

async function resolve(): Promise<SessionState> {
  if (apiBinding !== 'http') return { session: null, unguarded: true };

  const current = await client.auth.session.current().catch(() => null);
  if (current) return { session: current, unguarded: false };

  if (devLoginEmail) {
    const session = await client.auth.session.login({ email: devLoginEmail }).catch(() => null);
    return { session, unguarded: false };
  }
  return { session: null, unguarded: false };
}

/** Resolved once per load and cached, because the guard and the shell both ask and a second round trip would be a
 *  second chance to disagree. `forget()` is what a sign-out calls. */
export function sessionState(): Promise<SessionState> {
  pending ??= resolve();
  return pending;
}

export function forgetSession(): void {
  pending = null;
}

/** Hand the browser to the provider. A full navigation rather than a fetch: the OIDC flow is a redirect to Google
 *  and back, which is not something an XHR can do. */
export function startGoogleSignIn(returnTo: string): void {
  const next = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/home';
  window.location.assign(`${AUTH_ORIGIN}/auth/google/start?returnTo=${encodeURIComponent(next)}`);
}

export function signOut(): void {
  forgetSession();
  // Under the sim binding there is no server and no session to revoke: navigating to a logout endpoint that does
  // not exist would bounce through Vite's SPA fallback and LOOK like a bug. The signed-out page is the truthful
  // destination either way; the live binding reaches it via the server, which revokes the session on the way.
  if (AUTH_ORIGIN === '') {
    window.location.assign(`${SIGNED_OUT_PATH}?signedout=1`);
    return;
  }
  window.location.assign(`${AUTH_ORIGIN}/auth/logout`);
}
